import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Label } from "@shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { Textarea } from "@shared/ui/textarea";
import { Loader2, MessageSquare, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "@shared/ui/use-toast";

interface NoteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string | undefined;
  remoteUtilityId: number;
  useSendNote: (itemId: string) => any;
}

export function NoteDialog({
  isOpen,
  onClose,
  itemId,
  remoteUtilityId,
  useSendNote,
}: NoteDialogProps) {
  const [isInternal, setIsInternal] = useState<boolean>(false);
  const [messageText, setMessageText] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use the send note hook
  const sendNote = useSendNote(itemId || "");

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setIsInternal(false);
      setMessageText("");
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!messageText.trim() || !itemId) return;

    setIsSubmitting(true);

    try {
      // Prepare the note data using the correct CreateNoteInput structure
      const noteData = {
        requestId: Number(itemId),
        remoteUtilityId: Number(remoteUtilityId),
        notes: {
          note: messageText.trim(),
          noteType: isInternal ? 1 : 0, // 1 for internal, 0 for customer
        },
      };

      // Send the note using the hook
      await sendNote.mutateAsync(noteData);

      // Close dialog on success
      onClose();
    } catch (error) {
      console.error("Failed to add note:", error);
toast({
  title: "Error",
  description: `Failed to add note: ${error.message || "Unknown error"}`,
  variant: "destructive",
});
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Add Note
          </DialogTitle>
          <DialogDescription>
            Add a note or communication record for the selected consumer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <p className="text-sm text-gray-600">
              Note will be added to the consumer's record
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* <div>
              <Label htmlFor="visibility">Visibility</Label>
              <Select
                value={isInternal ? "internal" : "customer"}
                onValueChange={(v) => setIsInternal(v === "internal")}
                disabled={isSubmitting}
              >
                <SelectTrigger id="visibility" className="mt-2">
                  <SelectValue placeholder="Visibility" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">Internal Only</SelectItem>
                  <SelectItem value="customer">Customer Visible</SelectItem>
                </SelectContent>
              </Select>
            </div> */}
          </div>

          <div>
            <Label htmlFor="message">Message</Label>
            <div className="flex gap-2 mt-2">
              <Textarea
                id="message"
                placeholder="Type your message here..."
                className="resize-none flex-1"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                disabled={isSubmitting}
                rows={4}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={!messageText.trim() || isSubmitting}
              className="flex items-center gap-2"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {isSubmitting ? "Adding..." : "Add Note"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
